import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core'
import { Store, select } from '@ngrx/store'
import { SelectionModel } from '@angular/cdk/collections'
import { MatPaginator } from '@angular/material/paginator'
import { MatTableDataSource } from '@angular/material/table'
import { Connection } from '@tv3/store/connection/connection.model'
import {
  ActionFailed,
  Channel,
  ChannelService,
  ConnectedChannelAccountResponse,
  Destroyable,
  untilDestroy,
  ChannelNameTokens,
  DisconnectChannel,
  ConfirmDialogService,
  loadRentals,
  userFullname,
} from '@tokeet-frontend/tv3-platform'
import { AirbnbHelperService } from '../../../airbnb-helper.service'
import { Actions, ofType } from '@ngrx/effects'
import {
  LoadConnectionsByChannel,
  RefreshConnectionsForChannelComplete,
} from '@tv3/store/connection/connection.actions'
import { ChannelImportStatus, isTerminalImportStatus } from '@tv3/store/connection/connection.model'
import { ConnectionAirbnbService } from '@tv3/store/connection/connection-airbnb.service'
import { finalize, map, switchMap, tap } from 'rxjs/operators'
import * as lodash from 'lodash'

type AirbnbListing = ConnectedChannelAccountResponse['rooms'][number]

@Component({
  selector: 'app-connect-airbnb-wizard-complete2',
  templateUrl: './connect-airbnb-wizard-complete2.component.html',
  styleUrls: ['./connect-airbnb-wizard-complete2.component.scss'],
})
export class ConnectAirbnbWizardComplete2Component extends Destroyable implements OnInit {
  @ViewChild('paginator') set paginator(paginator: MatPaginator) {
    this.dataSource.paginator = paginator
  }
  displayedColumns: string[] = ['name', 'select']
  dataSource = new MatTableDataSource<AirbnbListing>()
  selection = new SelectionModel<AirbnbListing>(true, [])

  @Input() channel: Channel
  @Output() done = new EventEmitter()

  isLoading = true

  isImporting = false
  importedListings: string[] = []

  // Lifecycle of the listing-import job queued when the account connected; polled from
  // the backend instead of the blind five-second wait this screen used to make.
  importStatus: ChannelImportStatus | null = null
  importPollTimedOut = false
  isRetryingImport = false

  get userName() {
    const user = this.airbnbAccount?.user_info
    return userFullname({ firstName: user?.first_name, lastName: user?.last_name })
  }
  airbnbAccount: ConnectedChannelAccountResponse

  constructor(
    private store: Store<any>,
    private actions: Actions,
    private channelService: ChannelService,
    private confirmDialog: ConfirmDialogService,
    private airbnbHelperService: AirbnbHelperService,
    private connectionAirbnbService: ConnectionAirbnbService
  ) {
    super()
  }

  ngOnInit(): void {
    this.isLoading = true
    this.actions
      .pipe(
        ofType(RefreshConnectionsForChannelComplete),
        tap(() =>
          this.store.dispatch(LoadConnectionsByChannel({ channelName: this.channel.name, channelId: this.channel.id }))
        ),
        switchMap(() => this.channelService.getConnectedAccounts(this.channel.id)),
        map((accounts) => lodash.last(lodash.sortBy(accounts, (a) => a.createdOn))),
        untilDestroy(this)
      )
      .subscribe(
        (airbnbAccount) => {
          this.airbnbAccount = airbnbAccount
          this.dataSource.data = airbnbAccount?.rooms || []
          this.isLoading = false
          this.watchAutoImport()
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
          this.isLoading = false
        }
      )
  }

  // Follows the import job the backend queued while the account connected, so the screen
  // can show real progress and reload connections the moment the rentals actually exist.
  private watchAutoImport() {
    const channelId = (this.airbnbAccount?.channelId || this.channel.id) as string
    this.importStatus = null
    this.importPollTimedOut = false
    this.connectionAirbnbService
      .pollImportStatus(channelId)
      .pipe(untilDestroy(this))
      .subscribe(
        (status) => {
          // Transient request failures emit null; keep the last real status so the banner
          // survives them. A poll that gives up early is flagged on completion below.
          if (status) {
            this.importStatus = status
          }
          // 'skipped' is terminal too, but it means nothing was imported (the account
          // already had rentals at connect time), so there is nothing to refresh.
          if (isTerminalImportStatus(status) && status?.status !== 'skipped') {
            // Whatever the job managed to import exists now; refresh.
            this.store.dispatch(
              LoadConnectionsByChannel({ channelName: this.channel.name, channelId: this.channel.id })
            )
            this.store.dispatch(loadRentals({}))
          }
        },
        () => {},
        () => {
          // The poll gave up while a job was still running (very large accounts).
          this.importPollTimedOut = !!this.importStatus && !isTerminalImportStatus(this.importStatus)
        }
      )
  }

  get isAutoImportRunning() {
    return (
      !this.importPollTimedOut &&
      (this.importStatus?.status === 'queued' || this.importStatus?.status === 'in_progress')
    )
  }

  get isAutoImportDone() {
    return this.importStatus?.status === 'done'
  }

  get isAutoImportFailed() {
    return this.importStatus?.status === 'failed'
  }

  get autoImportText() {
    if (this.importPollTimedOut) {
      return $localize`The listing import is taking longer than expected. It keeps running in the background — progress is shown on the channel details page.`
    }
    const imported = this.importStatus?.imported || 0
    const total = this.importStatus?.total
    switch (this.importStatus?.status) {
      case 'queued':
        return $localize`Your listings are queued for import.`
      case 'in_progress':
        return total
          ? $localize`Importing your listings: ${imported} of ${total} imported so far.`
          : $localize`Importing your listings.`
      case 'done':
        return total ? $localize`Imported ${imported} of ${total} listings.` : $localize`Your listings were imported.`
      case 'failed':
        return $localize`Some of your listings could not be imported. You can retry now, or later from the channel details page.`
      default:
        return ''
    }
  }

  onRetryImport() {
    const channelId = (this.airbnbAccount?.channelId || this.channel.id) as string
    this.isRetryingImport = true
    this.connectionAirbnbService
      .importListings(channelId, this.airbnbAccount?.propertyId)
      .pipe(
        finalize(() => (this.isRetryingImport = false)),
        untilDestroy(this)
      )
      .subscribe(
        () => this.watchAutoImport(),
        (error) => this.store.dispatch(ActionFailed({ error }))
      )
  }

  onMasterChange(checked: boolean) {
    if (checked) {
      this.selection.select(...this.getSelectableListings())
    } else {
      this.selection.clear()
    }
  }

  isMasterChecked() {
    const listings = this.getSelectableListings()
    return listings.length > 0 && listings.every((item) => this.selection.isSelected(item))
  }

  onSearch(terms: string) {
    this.dataSource.filter = terms
  }

  onSelect(item: AirbnbListing, checked: boolean) {
    if (this.isAirbnbSyncPaused(item)) return

    if (checked) {
      this.selection.select(item)
    } else {
      this.selection.deselect(item)
    }
  }

  onImport() {
    this.isImporting = true
    const items = this.selection.selected.filter((item) => !this.isAirbnbSyncPaused(item))
    this.airbnbHelperService
      .importRooms(
        items.map(
          (t) =>
            ({
              airbnbDeleted: t.airbnb_deleted,
              airbnbListed: t.airbnb_listed,
              airbnbListedCheckedAt: t.airbnb_listed_checked_at,
              airbnbStatus: t.airbnb_status,
              // The full connection id of this account, not the base channel id: the
              // import job and its polled status are recorded against this entry.
              channelId: (this.airbnbAccount.channelId || this.channel.id) as string,
              roomId: t.id,
              propertyId: this.airbnbAccount.propertyId,
              syncPaused: t.sync_paused,
              syncPausedReason: t.sync_paused_reason,
            } as Connection)
        )
      )
      .pipe(
        finalize(() => (this.isImporting = false)),
        untilDestroy(this)
      )
      .subscribe((status) => {
        // A failed job keeps the rows selected so the same set can be retried; the helper
        // already toasts the outcome either way.
        if (!status || status.status === 'done') {
          this.importedListings.push(...items.map((t) => t.id))
          // An imported row renders a check instead of a checkbox, so leaving it selected would
          // keep the Import button live with nothing left to import.
          this.selection.deselect(...items)
        }
        this.store.dispatch(
          LoadConnectionsByChannel({
            channelName: ChannelNameTokens.AirBnBV2API,
            channelId: this.channel.id,
          })
        )
      })
  }

  get hasImported() {
    return this.importedListings.length > 0
  }

  isImported(item: AirbnbListing) {
    return this.importedListings.includes(item.id)
  }

  isAirbnbSyncPaused(item: AirbnbListing) {
    return !!item?.sync_paused || item?.airbnb_listed === false || !!item?.airbnb_deleted
  }

  private getSelectableListings() {
    return this.dataSource.data.filter((item) => !this.isImported(item) && !this.isAirbnbSyncPaused(item))
  }

  onDisconnect() {
    const channelId = this.airbnbAccount.channelId || this.channel.id
    const channelName = this.airbnbAccount.name || this.channel.name

    return this.confirmDialog
      .confirm({
        title: $localize`Disconnect this Account?`,
        body:
          $localize`This will disconnect this account from your AdvanceCM account.` +
          `<br/>` +
          $localize`Are you sure you want to Disconnect this account?`,
      })
      .subscribe(() => {
        this.store.dispatch(DisconnectChannel({ channelName, channelId }))
      })
  }
}
